import { Clinic } from '@interface/clinic';
import { Category, Discount, Product, Tag } from '@interface/product';

/**
 * Datos mínimos que necesitan las tarjetas de producto (ProductCard), los
 * carruseles y los filtros de listado. Los Server Components pasan esto a los
 * Client Components en vez del Product completo: el catálogo entero (con
 * clínicas, profesionales, textos largos…) se serializaba en cada página y
 * disparaba el HTML a ~2 MB.
 */
export type ProductCardData = Pick<
  Product,
  | 'id'
  | 'title'
  | 'description'
  | 'longDescription'
  | 'price'
  | 'isPack'
  | 'zone'
  | 'order'
  | 'hasUpgrades'
  | 'flowwwId'
  | 'type'
  | 'visibility'
  | 'visibleWeb'
  | 'productCardImagePosition'
  | 'numProductCardPhotos'
> & {
  category: Category[];
  tags: Tag[];
  clinicDetail: { clinic: Pick<Clinic, 'id' | 'internalName' | 'city'> }[];
  discounts: ProductDiscountLike[];
  extraInformation: { slug: string };
  creationDate?: string | Date;
};

export type ProductDiscountLike = Pick<
  Discount,
  'totalDiscount' | 'visibleLanding' | 'visibleDashboard' | 'visibleWeb'
> & {
  productDiscountClinics?: { clinicInfo: Pick<Clinic, 'id'> }[];
};

/** Lo que necesita `getDiscountedPrice`: precio y descuentos. */
export type ProductPriceLike = {
  price: number;
  discounts?: ProductDiscountLike[];
};

/** Lo que necesita `applyFilters` para decidir la visibilidad de un producto. */
export type ProductFilterable = Pick<
  Product,
  | 'category'
  | 'type'
  | 'title'
  | 'zone'
  | 'price'
  | 'description'
  | 'longDescription'
> & {
  clinicDetail: { clinic?: { internalName?: string } }[];
  visibility?: boolean;
};

export function toProductCardData(product: Product): ProductCardData {
  const withDate = product as Product & { creationDate?: string | Date };

  return {
    id: product.id,
    title: product.title,
    description: product.description,
    longDescription: product.longDescription,
    price: product.price,
    isPack: product.isPack,
    zone: product.zone,
    order: product.order,
    hasUpgrades: product.hasUpgrades,
    flowwwId: product.flowwwId,
    type: product.type,
    visibility: product.visibility,
    visibleWeb: product.visibleWeb,
    productCardImagePosition: product.productCardImagePosition,
    numProductCardPhotos: product.numProductCardPhotos,
    category: product.category ?? [],
    tags: product.tags ?? [],
    clinicDetail: (product.clinicDetail ?? []).map(item => ({
      clinic: {
        id: item.clinic?.id,
        internalName: item.clinic?.internalName,
        city: item.clinic?.city,
      },
    })),
    discounts: (product.discounts ?? []).map(discount => ({
      totalDiscount: discount.totalDiscount,
      visibleLanding: discount.visibleLanding,
      visibleDashboard: discount.visibleDashboard,
      visibleWeb: discount.visibleWeb,
      productDiscountClinics: (discount.productDiscountClinics ?? []).map(
        item => ({ clinicInfo: { id: item.clinicInfo?.id } })
      ),
    })),
    extraInformation: { slug: product.extraInformation?.slug },
    creationDate: withDate.creationDate,
  };
}

export function toProductCardDataList(products: Product[]): ProductCardData[] {
  return (products ?? []).map(toProductCardData);
}

/**
 * Mismo orden que el carrusel de la ficha (por nombre de archivo, numérico:
 * …-1.jpg, …-2.jpg…). La API las devuelve desordenadas y el og:image cogía
 * `[0]`, así que al compartir el enlace salía una foto distinta a la primera.
 */
export function sortBeforeAndAfterImages<T extends { urlBefore?: string }>(
  images: T[]
): T[] {
  return [...images].sort((a, b) =>
    (a.urlBefore ?? '').localeCompare(b.urlBefore ?? '', undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
}

function slimClinic<T>(clinic: T): T {
  if (!clinic || typeof clinic !== 'object') return clinic;
  const { professionals: _professionals, ...rest } = clinic as {
    professionals?: unknown;
  };
  return rest as T;
}

/**
 * Quita del producto lo que la web no usa y pesa mucho: la API devuelve en
 * cada producto copias anidadas de otros productos (`relatedProducts`,
 * `clinicDetail[].product`) y la ficha completa de cada clínica con todos
 * sus profesionales. Un solo producto llegaba a ~470 KB de JSON.
 */
export function slimProductForWeb<T extends Partial<Product>>(product: T): T {
  if (!product || typeof product !== 'object') return product;

  const slim: Record<string, any> = { ...product };
  delete slim.relatedProducts;

  if (Array.isArray(slim.beforeAndAfterImages)) {
    slim.beforeAndAfterImages = sortBeforeAndAfterImages(
      slim.beforeAndAfterImages
    );
  }

  if (Array.isArray(slim.clinicDetail)) {
    slim.clinicDetail = slim.clinicDetail.map((item: any) => {
      if (!item || typeof item !== 'object') return item;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { product: _nestedProduct, ...rest } = item;
      return { ...rest, clinic: slimClinic(rest.clinic) };
    });
  }

  if (Array.isArray(slim.upgrades)) {
    slim.upgrades = slim.upgrades.map((upgrade: any) =>
      upgrade && upgrade.product
        ? { ...upgrade, product: slimProductForWeb(upgrade.product) }
        : upgrade
    );
  }

  if (Array.isArray(slim.discounts)) {
    slim.discounts = slim.discounts.map((discount: any) =>
      discount && Array.isArray(discount.productDiscountClinics)
        ? {
            ...discount,
            productDiscountClinics: discount.productDiscountClinics.map(
              (item: any) =>
                item
                  ? { ...item, clinicInfo: slimClinic(item.clinicInfo) }
                  : item
            ),
          }
        : discount
    );
  }

  return slim as T;
}
